import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../utils/firebaseconfig';
import "../styles/Login.css";
import bellicon from '../assets/bell-icon.png';

function Login() {
  const navigate = useNavigate();

  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phonePattern = /^[0-9]{10,15}$/;

 const handleRegister = async () => {
  setError('');
  setIsLoading(true);

  if (!email || !password || !confirmPassword || !username || !phone) {
    setError('Please fill in all fields.');
    setIsLoading(false);
    return;
  }

  if (!emailPattern.test(email)) {
    setError('Please enter a valid email address.');
    setIsLoading(false);
    return;
  }

  if (password.length < 6) {
    setError('Password must be at least 6 characters.');
    setIsLoading(false);
    return;
  }

  if (password !== confirmPassword) {
    setError('Passwords do not match.');
    setIsLoading(false);
    return;
  }

  if (username.length < 3) {
    setError('Username should be at least 3 characters.');
    setIsLoading(false);
    return;
  }

  if (!phonePattern.test(phone)) {
    setError('Enter a valid phone number with 10–15 digits.');
    setIsLoading(false);
    return;
  }

  try {
    // Create the user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Send email verification
    await sendEmailVerification(user);

    // Save user info in Firestore
    await setDoc(doc(db, 'User', user.uid), {
      username,
      profilePic: bellicon,
      bio: '',
      followersCount: 0,
      followingCount: 0,
      products: [],
      posts: [],
      auctions: [],
      createdAt: new Date().toISOString(),
      contact: {
        email,
        phone,
      },
    });

    // Show a message about verification but don't sign out
    alert('Verification email sent. Please check your inbox to verify your email. You can continue using the app while waiting for verification.');

    // Navigate to homepage - user is already logged in
    navigate('/');
  } catch (error) {
    console.error('Error registering user:', error.message);
    setError(error.message);
  } finally {
    setIsLoading(false);
  }
};


  const handleLogin = async () => {
    setError('');
    setIsLoading(true);

    if (!email || !password) {
      setError('Please enter both email and password.');
      setIsLoading(false);
      return;
    }

    if (!emailPattern.test(email)) {
      setError('Invalid email format.');
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      setIsLoading(false);
      return;
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // If email is not verified, show a warning but let them proceed
      if (!user.emailVerified) {
        // Re-send verification email if needed
        await sendEmailVerification(user);
        alert('Your email is not verified yet. A new verification email has been sent. You can continue using the app, but some features may be limited until verification is complete.');
      }

      // Allow login regardless of verification status
      navigate('/');
    } catch (error) {
      console.error('Error logging in:', error.message);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userRef = doc(db, 'User', user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          username: user.displayName || '',
          profilePic: user.photoURL || '',
          bio: '',
          followersCount: 0,
          followingCount: 0,
          products: [],
          posts: [],
          auctions: [],
          createdAt: new Date().toISOString(),
          contact: {
            email: user.email,
            phone: '',
          },
        });
      }

      navigate('/');
    } catch (error) {
      console.error('Google Login Error:', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      {isLoading && <div className="loading-overlay"><div className="loader"></div></div>}

      <h2>{isRegistering ? 'Register' : 'Login'}</h2>

      {error && <p className="error">{error}</p>}

      <input
        className='input-login'
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <input
        className='input-login'
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />

      {isRegistering && (
        <>
          <input
            className='input-login'
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <input
            className='input-login'
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            className='input-login'
            type="text"
            placeholder="Phone Number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </>
      )}

      <button className='button-login' onClick={isRegistering ? handleRegister : handleLogin}>
        {isRegistering ? 'Register' : 'Login'}
      </button>
      <button className='button-login' onClick={handleGoogleLogin}>Login using Google</button>

      <p onClick={() => setIsRegistering(!isRegistering)} style={{ cursor: 'pointer' }}>
        {isRegistering ? 'Already have an account? Login' : "Don't have an account? Register"}
      </p>
    </div>
  );
}

export default Login;
